import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { Provider, useDispatch } from 'react-redux';
import store from './store/index';
import { initializeAuthListener } from './actions/auth-actions';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import { useIsDarkMode } from './hooks/useIsDarkMode';
import './App.css';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';

import TestPage from './components/TestPage';
import HomePage from './components/HomePage';
import StashCardPage from './components/stashcard/StashCardPage';
import { CategoryDetailPage } from './components/category/CategoryDetailPage';
import GroupPage from './components/group/GroupPage';
import VendorPage from './components/vendor/VendorPage';
import CommunityPage from './components/community/CommunityPage';
import VendorEditPage from './components/vendor/VendorEditPage';
import CommunityEditPage from './components/community/CommunityEditPage';
import MagicLinkPage from './components/auth/MagicLinkPage';
import StashBoardPage from './components/StashBoardPage';
import { SnapStashPage } from './components/snap-stash/SnapStashPage';
import { ComingSoonPage } from './components/common/ComingSoonPage';
import { AppShell } from './components/app-shell';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import NavMenu from './components/navigation/NavMenu';
import ChatPage from './components/chat/ChatPage';
import CommunityListPage from './components/community/CommunityListPage';
import ConnectedAppsPage from './components/connected-apps/ConnectedAppsPage';
import NotFound from './components/NotFound';
import { LandingLayout } from './components/landing/LandingLayout';

import { styled } from '@mui/material/styles';
import type { AppDispatch } from './store';

const RootContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const StyledAppBar = styled(AppBar)({
  backgroundColor: '#3995f6',
});

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const HeaderContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StashLogo = styled('img')(({ theme: t }) => ({
  paddingLeft: t.spacing(1),
  paddingTop: t.spacing(1),
  height: '80px',
}));

const MainContent = styled('main')(({ theme: t }) => ({
  flexGrow: 1,
  padding: t.spacing(3),
}));

function AuthInitializer(): null {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(initializeAuthListener());
  }, [dispatch]);

  return null;
}

interface LayoutProps {
  children: React.ReactNode;
}

function Copyright(): React.JSX.Element {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <a href="https://stash-app.com">Stash </a>
      {' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

function Admin({ children }: LayoutProps): React.JSX.Element {
  return (
    <RootContainer>
      <StyledAppBar position="static" elevation={0}>
        <StyledToolbar>
          <HeaderContainer>
            <StashLogo src="/stash_logo_white.png" alt="Stash" />
            <span style={{ marginLeft: '20px', color: 'white', marginTop: '45px' }}>Admin</span>
          </HeaderContainer>
        </StyledToolbar>
      </StyledAppBar>

      <NavMenu />
      <MainContent>
        <Container>
          <Box my={4}>{children}</Box>
        </Container>

        <Copyright />
      </MainContent>
    </RootContainer>
  );
}

function App(): React.JSX.Element {
  const isDark = useIsDarkMode();
  const theme = React.useMemo(() => createAppTheme(isDark ? 'dark' : 'light'), [isDark]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Provider store={store}>
          <AuthInitializer />
          <Router>
            <Routes>
              <Route
                path="/admin/test"
                element={
                  <ProtectedRoute>
                    <Admin>
                      <TestPage />
                    </Admin>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/vendor-edit"
                element={
                  <ProtectedRoute>
                    <Admin>
                      <VendorEditPage />
                    </Admin>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/community-edit"
                element={
                  <ProtectedRoute>
                    <Admin>
                      <CommunityEditPage />
                    </Admin>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <Admin>
                      <NotFound />
                    </Admin>
                  </ProtectedRoute>
                }
              />

              {/* Auth/magic-link routes — keep the public landing layout, outside AppShell */}
              <Route
                path="/app/login"
                element={
                  <LandingLayout>
                    <Login />
                  </LandingLayout>
                }
              />
              <Route
                path="/ios/log-me-in/:code"
                element={
                  <LandingLayout contained>
                    <MagicLinkPage />
                  </LandingLayout>
                }
              />

              {/* All /app/* routes share AppShell (replaces AppScopedLayout) */}
              <Route element={<AppShell />}>
                <Route path="/app/stashboard" element={<StashBoardPage />} />
                <Route path="/app/snap" element={<SnapStashPage />} />
                {/* iOS-only features — "coming soon" landings on web */}
                <Route path="/app/friends" element={<ComingSoonPage feature="Friends" icon="🤝" />} />
                <Route path="/app/groups" element={<ComingSoonPage feature="Groups" icon="👥" />} />
                <Route path="/app/messages" element={<ComingSoonPage feature="Messages" icon="💬" />} />
                <Route
                  path="/app/connected-apps"
                  element={
                    <ProtectedRoute>
                      <ConnectedAppsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/app/stashcards/:id" element={<StashCardPage />} />
                <Route path="/app/categories/:categoryId" element={<CategoryDetailPage />} />
                <Route
                  path="/app/chat"
                  element={
                    <ProtectedRoute>
                      <ChatPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/app/vendors/:id"
                  element={
                    <ProtectedRoute>
                      <VendorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/app/groups/:id"
                  element={
                    <ProtectedRoute>
                      <GroupPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/app/communities" element={<CommunityListPage />} />
                <Route path="/app/communities/:id" element={<CommunityPage />} />
              </Route>
              <Route
                path="/"
                element={
                  <LandingLayout>
                    <HomePage />
                  </LandingLayout>
                }
              />

              <Route
                path="*"
                element={
                  <LandingLayout contained>
                    <NotFound />
                  </LandingLayout>
                }
              />
            </Routes>
          </Router>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
